import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Calendar, ChevronDown, Sunrise, Sun, Moon, List, Check, Star } from 'lucide-react';
import specialtyService from '../services/specialtyService';
import { doctorService } from '../services/doctorService';
import { isSlotBookable } from '../services/scheduleModels';
import florLogo from '../assets/florDaisiesSF.png';

// The 5 most-used specialties — used for the "Todas" default
const TODAS_SPECIALTY_IDS = [16, 15, 17, 18, 28];
const TODAS_TAB = { id: 'todas', name: 'Todas' };

const CACHE_KEY = 'daisies_schedule_cache';

// ─── Helpers ───
const saveToCache = (data, selectedId) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      availabilityByDate: data,
      selectedSpecialtyId: selectedId,
      timestamp: Date.now(),
    }));
  } catch (_) {}
};

const loadFromCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Expire after 5 minutes
    if (Date.now() - parsed.timestamp > 5 * 60 * 1000) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    return null;
  }
};

const ScheduleSection = ({ onStartBooking }) => {
  const [specialties, setSpecialties] = useState([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState(TODAS_TAB);
  const [specialtyConfigs, setSpecialtyConfigs] = useState({});
  const [loading, setLoading] = useState(true);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [availabilityByDate, setAvailabilityByDate] = useState({});
  const [consultationType, setConsultationType] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);
  const [showSpecialtyDropdown, setShowSpecialtyDropdown] = useState(false);
  const cancelledRef = useRef(false);
  const dropdownRef = useRef(null);
  const restoredFromCacheRef = useRef(false);
  const userChangedSpecialtyRef = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowSpecialtyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load specialties + configs on mount; restore cache
  useEffect(() => {
    const load = async () => {
      try {
        const specs = await specialtyService.getFormattedOnDemandSpecialties();
        setSpecialties(specs);
        try {
          const configs = await specialtyService.getSpecialtyConfig();
          setSpecialtyConfigs(configs || {});
        } catch (_) {}

        // Restore cache
        const cached = loadFromCache();
        if (cached && cached.availabilityByDate && Object.keys(cached.availabilityByDate).length > 0) {
          setAvailabilityByDate(cached.availabilityByDate);
          restoredFromCacheRef.current = true;
          // Restore selected specialty
          if (cached.selectedSpecialtyId === 'todas') {
            setSelectedSpecialty(TODAS_TAB);
          } else {
            const found = specs.find(s => s.id === cached.selectedSpecialtyId);
            if (found) setSelectedSpecialty(found);
          }
        }
      } catch (err) {
        console.warn('[ScheduleSection] Error loading specialties:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const isTodas = selectedSpecialty?.id === 'todas';

  const currentConfig = (!isTodas && selectedSpecialty) ? specialtyConfigs[selectedSpecialty.id] : null;

  const activeDurationId = useMemo(() => {
    if (isTodas) return null;
    if (!currentConfig) {
      return selectedSpecialty?.durationId || selectedSpecialty?.extraDurationId || 109;
    }
    if (consultationType === 'extra') {
      return currentConfig.extraDuration || currentConfig.duration || 109;
    }
    return currentConfig.duration || 109;
  }, [currentConfig, consultationType, selectedSpecialty, isTodas]);

  // Merge slot data progressively
  const mergeAvailability = useCallback((newSlots) => {
    setAvailabilityByDate(prev => {
      const updated = { ...prev };
      Object.entries(newSlots).forEach(([dateKey, slots]) => {
        if (!updated[dateKey]) updated[dateKey] = [];
        const existing = new Set(updated[dateKey].map(s => `${s.id}-${s.init}-${s.specialtyId || ''}`));
        slots.forEach(slot => {
          const key = `${slot.id}-${slot.init}-${slot.specialtyId || ''}`;
          if (!existing.has(key)) {
            updated[dateKey].push(slot);
            existing.add(key);
          }
        });
        updated[dateKey].sort((a, b) => {
          const tA = a.init.split(':').map(Number);
          const tB = b.init.split(':').map(Number);
          return (tA[0] * 60 + (tA[1] || 0)) - (tB[0] * 60 + (tB[1] || 0));
        });
      });
      return updated;
    });
  }, []);

  // Process raw API response — filter to only allowedDates (4-day window)
  const processApiResponse = useCallback((data, date, isExtra, specialtyMeta, allowedDates) => {
    const organized = {};
    if (!data) return organized;
    const dayArray = data.result || data;
    if (!Array.isArray(dayArray)) return organized;

    // Build a Set of allowed date strings for fast lookup
    const allowedSet = allowedDates ? new Set(allowedDates) : null;

    dayArray.forEach(dayData => {
      const dateKey = dayData.date || date;
      // Skip dates outside the 4-day window
      if (allowedSet && !allowedSet.has(dateKey)) return;
      if (!organized[dateKey]) organized[dateKey] = [];
      const slots = dayData.schedule_available || [];
      slots.forEach(slot => {
        // Solo horarios agendables: descarta pasados y los que están dentro del
        // mínimo de anticipación (>= now + 6h), igual que los selectores de
        // agendar y el backend. Sin esto el browse mostraba slots no agendables.
        if (!isSlotBookable(dateKey, slot.init)) return;
        organized[dateKey].push({
          id: slot.id,
          init: slot.init,
          end: slot.end,
          professional_ids: slot.professional_ids || [],
          date: dateKey,
          week_day: dayData.week_day,
          isExtra: isExtra,
          duration_id: slot.duration_id,
          ...(specialtyMeta ? {
            specialtyId: specialtyMeta.id,
            specialtyName: specialtyMeta.name,
            specialtyIcon: specialtyMeta.icon,
          } : {}),
        });
      });
    });
    return organized;
  }, []);

  // Load availability
  useEffect(() => {
    // Skip re-fetch if we just restored from cache and user hasn't changed specialty
    if (restoredFromCacheRef.current && !userChangedSpecialtyRef.current) {
      restoredFromCacheRef.current = false;
      return;
    }
    userChangedSpecialtyRef.current = false;

    cancelledRef.current = true;
    cancelledRef.current = false;
    const localCancelled = cancelledRef;

    if (isTodas) {
      setSlotsLoading(true);
      setAvailabilityByDate({});

      const today = new Date();
      const dates = [];
      for (let i = 0; i < 4; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        dates.push(d.toISOString().split('T')[0]);
      }

      const specsToFetch = TODAS_SPECIALTY_IDS.map(id => {
        const spec = specialties.find(s => s.id === id);
        const config = specialtyConfigs[id];
        if (!spec && !config) return null;
        const durationId = config?.duration || spec?.durationId || 109;
        return {
          id,
          durationId,
          name: config?.name || spec?.name || '',
          icon: config?.icon || spec?.icon || '',
        };
      }).filter(Boolean);

      if (specsToFetch.length === 0) {
        setSlotsLoading(false);
        return;
      }

      let remaining = specsToFetch.length * dates.length;
      setPendingCount(remaining);
      const allNewSlots = {};

      specsToFetch.forEach(specMeta => {
        dates.forEach(date => {
          doctorService.getAvailabilityByDatePublic(
            specMeta.id, specMeta.durationId, date, false, 'America/Mexico_City'
          )
            .then(res => {
              if (localCancelled.current) return;
              const organized = processApiResponse(res, date, false, specMeta, dates);
              mergeAvailability(organized);
              // Accumulate for cache
              Object.entries(organized).forEach(([dk, sl]) => {
                if (!allNewSlots[dk]) allNewSlots[dk] = [];
                allNewSlots[dk].push(...sl);
              });
            })
            .catch(() => {})
            .finally(() => {
              if (localCancelled.current) return;
              remaining--;
              setPendingCount(remaining);
              if (remaining <= 0) {
                setSlotsLoading(false);
                // Save to cache
                setAvailabilityByDate(prev => {
                  saveToCache(prev, 'todas');
                  return prev;
                });
              }
            });
        });
      });

      return () => { localCancelled.current = true; };
    }

    // Single specialty
    if (!selectedSpecialty) return;

    const loadAvailability = async () => {
      setSlotsLoading(true);
      setAvailabilityByDate({});

      try {
        const today = new Date();
        const dates = [];
        for (let i = 0; i < 4; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() + i);
          dates.push(d.toISOString().split('T')[0]);
        }

        const durationIds = [];
        if (consultationType === 'all' && currentConfig) {
          if (currentConfig.duration) durationIds.push({ id: currentConfig.duration, isExtra: false });
          if (currentConfig.extraDuration && currentConfig.extraDuration !== currentConfig.duration) {
            durationIds.push({ id: currentConfig.extraDuration, isExtra: true });
          }
        }
        if (durationIds.length === 0) {
          durationIds.push({ id: activeDurationId, isExtra: consultationType === 'extra' });
        }

        const fetches = [];
        for (const date of dates) {
          for (const dur of durationIds) {
            fetches.push(
              doctorService.getAvailabilityByDatePublic(
                selectedSpecialty.id, dur.id, date, false, 'America/Mexico_City'
              ).then(res => ({ date, data: res, isExtra: dur.isExtra }))
               .catch(() => ({ date, data: null, isExtra: dur.isExtra }))
            );
          }
        }

        const results = await Promise.all(fetches);
        if (localCancelled.current) return;

        const organized = {};
        results.forEach(({ date, data, isExtra }) => {
          const partial = processApiResponse(data, date, isExtra, null, dates);
          Object.entries(partial).forEach(([dateKey, slots]) => {
            if (!organized[dateKey]) organized[dateKey] = [];
            organized[dateKey].push(...slots);
          });
        });

        Object.keys(organized).forEach(dateKey => {
          const seen = new Set();
          organized[dateKey] = organized[dateKey].filter(slot => {
            const key = `${slot.id}-${slot.init}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          organized[dateKey].sort((a, b) => {
            const tA = a.init.split(':').map(Number);
            const tB = b.init.split(':').map(Number);
            return (tA[0] * 60 + (tA[1] || 0)) - (tB[0] * 60 + (tB[1] || 0));
          });
        });

        setAvailabilityByDate(organized);
        saveToCache(organized, selectedSpecialty.id);
      } catch (err) {
        console.warn('[ScheduleSection] Error loading availability:', err);
      } finally {
        if (!localCancelled.current) setSlotsLoading(false);
      }
    };

    loadAvailability();
    return () => { localCancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpecialty, consultationType, activeDurationId, currentConfig, specialties, specialtyConfigs, isTodas]);

  const formatTime = (time) => {
    const [h, m] = time.split(':');
    const hr = parseInt(h);
    const ampm = hr >= 12 ? 'PM' : 'AM';
    return `${hr % 12 || 12}:${m} ${ampm}`;
  };

  const formatDayLabel = (dateStr) => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];
    if (dateStr === todayStr) return 'Hoy';
    if (dateStr === tomorrowStr) return 'Mañana';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const groupByTimeOfDay = (slots) => {
    const groups = { morning: [], afternoon: [], evening: [] };
    slots.forEach(slot => {
      const hour = parseInt(slot.init.split(':')[0]);
      if (hour >= 7 && hour < 12) groups.morning.push(slot);
      else if (hour >= 12 && hour < 18) groups.afternoon.push(slot);
      else groups.evening.push(slot);
    });
    return groups;
  };

  const sortedDates = Object.keys(availabilityByDate).sort();
  const totalSlots = sortedDates.reduce((sum, d) => sum + availabilityByDate[d].length, 0);

  const abbreviateName = (name) => {
    if (!name) return '';
    const map = {
      'Psicología': 'Psico',
      'Ginecología': 'Gineco',
      'Nutrición': 'Nutri',
      'Salud General y Skincare': 'Salud',
      'Entrenamiento': 'Entreno',
    };
    return map[name] || name.slice(0, 6);
  };

  const currentSpecialtyDisplay = isTodas
    ? TODAS_TAB
    : specialties.find(s => s.id === selectedSpecialty?.id) || selectedSpecialty;

  // Slot button — chip violeta uniforme; el grupo (Mañana/Tarde/Noche) da el contexto.
  const SLOT_CLASSES = 'bg-brand-tint text-brand-deep hover:bg-brand hover:text-white';

  const renderSlotButton = (slot, i) => {
    const specId = isTodas ? (slot.specialtyId || selectedSpecialty?.id) : selectedSpecialty?.id;
    const doctor = slot.professional_ids?.[0];
    const doctorName = doctor?.name || '';
    const avatar = doctor?.avatar;

    return (
      <button
        key={`${slot.id}-${slot.init}-${slot.specialtyId || ''}-${i}`}
        onClick={() => onStartBooking(specId, { ...slot, time: slot.init })}
        className={`press-scale font-medium rounded-card-sm transition-colors text-center flex flex-col items-center justify-center py-2 px-1 gap-1 ${SLOT_CLASSES} ${
          isTodas ? 'text-[10px] sm:text-xs' : 'text-xs'
        }`}
      >
        <span className="font-bold">{formatTime(slot.init)}</span>

        <div className="flex items-center gap-1 bg-white/55 rounded-full px-1.5 py-0.5 max-w-[95%]">
          {doctor && (
            <img
              src={avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'D')}&background=786ED9&color=fff`}
              className="w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full object-cover border border-white/50"
              alt=""
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'D')}&background=786ED9&color=fff`;
              }}
            />
          )}
          <span className="text-[8px] sm:text-[9px] font-bold truncate leading-none text-brand-deep">
            {doctorName || abbreviateName(slot.specialtyName)}
          </span>
        </div>

        {isTodas && slot.specialtyName && !doctorName && (
          <span className="block text-[8px] opacity-70 truncate leading-tight">
            {abbreviateName(slot.specialtyName)}
          </span>
        )}
      </button>
    );
  };

  // Render time group block
  const renderTimeGroup = (label, Icon, slots) => {
    if (slots.length === 0) return null;
    return (
      <div>
        <p className="text-[10px] sm:text-xs font-semibold text-brand-deep/70 mb-1 sm:mb-2 flex items-center gap-1.5">
          <Icon size={12} className="text-brand" aria-hidden="true" /> {label}
        </p>
        {/* Desktop: grid, Mobile: flex wrap for larger buttons */}
        <div className={`hidden md:grid gap-1 sm:gap-1.5 ${isTodas ? 'grid-cols-2 sm:grid-cols-2' : 'grid-cols-3 sm:grid-cols-2'}`}>
          {slots.map((slot, i) => renderSlotButton(slot, i))}
        </div>
        <div className="flex md:hidden flex-wrap gap-1.5">
          {slots.map((slot, i) => {
            const specId = isTodas ? (slot.specialtyId || selectedSpecialty?.id) : selectedSpecialty?.id;
            const doctor = slot.professional_ids?.[0];
            const doctorName = doctor?.name || '';
            const avatar = doctor?.avatar;

            return (
              <button
                key={`m-${slot.id}-${slot.init}-${slot.specialtyId || ''}-${i}`}
                onClick={() => onStartBooking(specId, { ...slot, time: slot.init })}
                className={`press-scale font-medium rounded-card-sm transition-colors text-center text-xs px-3 py-2 flex flex-col items-center gap-1 ${SLOT_CLASSES}`}
              >
                <span className="font-bold">{formatTime(slot.init)}</span>
                <div className="flex items-center gap-1 bg-white/55 rounded-full px-1.5 py-0.5">
                  {doctor && (
                    <img
                      src={avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'D')}&background=786ED9&color=fff`}
                      className="w-3 h-3 rounded-full object-cover border border-white/50"
                      alt=""
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctorName || 'D')}&background=786ED9&color=fff`;
                      }}
                    />
                  )}
                  <span className="text-[9px] font-bold truncate max-w-[60px] leading-none text-brand-deep">
                    {doctorName || abbreviateName(slot.specialtyName)}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center">
        <img src={florLogo} alt="Cargando" className="h-12 w-12 animate-spin-slow" />
        <p className="mt-3 text-sm text-brand-deep/70 font-medium">Cargando horarios...</p>
      </div>
    );
  }

  const hasAnySlots = sortedDates.length > 0;
  const isFullyLoaded = !slotsLoading;
  const showGrid = hasAnySlots || slotsLoading;

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 pb-8 sm:pb-12">
      {/* Section header */}
      <div className="text-center mb-4 sm:mb-6 pt-6 sm:pt-10">
        <h2 className="text-[clamp(1.4rem,4vw,2.6rem)] font-bold text-brand-deep tracking-[-0.025em] mb-1 sm:mb-3 text-balance">
          Todos los <span className="text-brand">horarios</span> disponibles.
        </h2>
        <p className="text-text-body text-xs sm:text-base md:text-lg max-w-2xl mx-auto">
          Explora los horarios abiertos por especialidad y agenda en un clic.
        </p>
      </div>

      {/* Specialty dropdown */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="relative flex-1 sm:flex-initial sm:min-w-[240px]" ref={dropdownRef}>
          <button
            onClick={() => setShowSpecialtyDropdown(!showSpecialtyDropdown)}
            className="w-full px-4 py-3 rounded-card-md bg-surface-card transition-colors text-left flex items-center justify-between gap-2"
          >
            <div>
              <span className="text-[10px] uppercase tracking-[0.08em] text-brand-deep/60 font-bold block">Especialidad</span>
              <span className="text-sm font-semibold text-brand-deep">
                {currentSpecialtyDisplay?.name || 'Todas'}
              </span>
            </div>
            <ChevronDown size={16} className={`text-brand transition-transform ${showSpecialtyDropdown ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>

          {showSpecialtyDropdown && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-surface-card rounded-card-md shadow-elevated border border-brand-100 z-50 max-h-64 overflow-y-auto">
              {/* Todas option */}
              <button
                onClick={() => {
                  userChangedSpecialtyRef.current = true;
                  setSelectedSpecialty(TODAS_TAB);
                  setShowSpecialtyDropdown(false);
                }}
                className={`w-full px-4 py-2.5 text-left text-sm hover:bg-brand-50 transition-colors flex items-center gap-2 ${
                  isTodas ? 'bg-brand-50 text-brand font-semibold' : 'text-brand-deep'
                }`}
              >
                Todas
              </button>
              {specialties.map(spec => (
                <button
                  key={spec.id}
                  onClick={() => {
                    userChangedSpecialtyRef.current = true;
                    setSelectedSpecialty(spec);
                    setShowSpecialtyDropdown(false);
                  }}
                  className={`w-full px-4 py-2.5 text-left text-sm hover:bg-brand-50 transition-colors flex items-center gap-2 ${
                    selectedSpecialty?.id === spec.id ? 'bg-brand-50 text-brand font-semibold' : 'text-brand-deep'
                  }`}
                >
                  {spec.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Consultation type filter (single specialty only) */}
        {!isTodas && (
          <div className="inline-flex bg-surface-card border border-brand-100 rounded-card-md p-1 gap-1">
            {[
              { key: 'all', label: 'Todas', Icon: List },
              { key: 'included', label: 'Incluida', Icon: Check },
              { key: 'extra', label: 'Extra', Icon: Star },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setConsultationType(opt.key)}
                className={`press-scale px-3 sm:px-4 py-1.5 sm:py-2 rounded-card-sm text-xs sm:text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                  consultationType === opt.key
                    ? 'bg-brand text-white'
                    : 'text-brand-deep/70 hover:bg-brand-50'
                }`}
              >
                <opt.Icon size={13} aria-hidden="true" />
                <span>{opt.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Schedule grid / list */}
      {!isTodas && slotsLoading && sortedDates.length === 0 ? (
        <div className="py-10 flex flex-col items-center justify-center">
          <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin-slow" />
          <p className="mt-3 text-sm text-brand-deep/70 font-medium">
            Cargando horarios de {selectedSpecialty?.name || 'la especialidad'}...
          </p>
        </div>
      ) : !showGrid && isFullyLoaded ? (
        <div className="text-center py-10 sm:py-16 bg-surface-card rounded-card">
          <Calendar className="mx-auto mb-3 sm:mb-4 text-brand/60" size={36} aria-hidden="true" />
          <h3 className="text-base sm:text-lg font-semibold text-brand-deep mb-2">Sin horarios disponibles</h3>
          <p className="text-text-body text-xs sm:text-sm">
            No hay horarios disponibles para {isTodas ? 'estas especialidades' : (selectedSpecialty?.name || 'esta especialidad')}
            {!isTodas && consultationType !== 'all' ? ` (${consultationType === 'included' ? 'incluida' : 'extra'})` : ''} en los próximos días.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <h3 className="text-sm sm:text-lg font-bold text-brand-deep">
              {isTodas ? (
                <>Todas las especialidades</>
              ) : (
                <>
                  {selectedSpecialty?.name}
                  {consultationType !== 'all' && (
                    <span className="text-xs sm:text-sm font-medium text-text-mute ml-2">
                      ({consultationType === 'included' ? 'Consulta incluida' : 'Consulta extra'})
                    </span>
                  )}
                </>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {slotsLoading && pendingCount > 0 && (
                <span className="text-[10px] sm:text-xs text-brand animate-pulse font-medium">
                  Cargando...
                </span>
              )}
              <span className="text-[10px] sm:text-sm text-text-mute">{totalSlots} disponibles</span>
            </div>
          </div>

          {/* DESKTOP: grid layout (hidden on mobile) */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {sortedDates.map(dateKey => {
              const slots = availabilityByDate[dateKey];
              const groups = groupByTimeOfDay(slots);
              return (
                <div key={dateKey} className="bg-surface-card rounded-card p-5">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-brand-100">
                    <Calendar size={16} className="text-brand" aria-hidden="true" />
                    <h4 className="font-bold text-brand-deep text-sm capitalize">
                      {formatDayLabel(dateKey)}
                    </h4>
                    <span className="ml-auto text-xs text-brand-deep/70 bg-brand-50 px-2 py-0.5 rounded-full">
                      {slots.length}
                    </span>
                  </div>
                  <div className="space-y-4">
                    {renderTimeGroup('Mañana', Sunrise, groups.morning)}
                    {renderTimeGroup('Tarde', Sun, groups.afternoon)}
                    {renderTimeGroup('Noche', Moon, groups.evening)}
                    {slots.length === 0 && (
                      <p className="text-xs text-text-mute text-center py-4">Sin horarios</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Placeholder cards with spinning flower */}
            {isTodas && slotsLoading && sortedDates.length < 4 && (
              <>
                {sortedDates.length === 0 && (
                  <div className="col-span-full flex flex-col items-center justify-center py-6">
                    <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin-slow" />
                    <p className="mt-2 text-xs text-brand-deep/70">Cargando horarios...</p>
                  </div>
                )}
                {Array.from({ length: 4 - sortedDates.length }).map((_, i) => (
                  <div key={`placeholder-${i}`} className="bg-surface-card rounded-card p-5 animate-pulse">
                    <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-brand-100">
                      <div className="w-3 h-3 bg-brand-100 rounded" />
                      <div className="w-16 h-3 bg-brand-100 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-12 h-2.5 bg-brand-50 rounded" />
                      <div className="grid grid-cols-2 gap-1">
                        {Array.from({ length: 4 }).map((_, j) => (
                          <div key={j} className="h-7 bg-brand-50 rounded-card-sm" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* MOBILE: list layout (shown only on mobile) */}
          <div className="flex flex-col gap-3 md:hidden">
            {sortedDates.map(dateKey => {
              const slots = availabilityByDate[dateKey];
              const groups = groupByTimeOfDay(slots);
              return (
                <div key={dateKey} className="bg-surface-card rounded-card p-4">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-brand-100">
                    <Calendar size={14} className="text-brand" aria-hidden="true" />
                    <h4 className="font-bold text-brand-deep text-base capitalize">
                      {formatDayLabel(dateKey)}
                    </h4>
                    <span className="ml-auto text-xs text-brand-deep/70 bg-brand-50 px-2 py-0.5 rounded-full">
                      {slots.length} horarios
                    </span>
                  </div>
                  <div className="space-y-3">
                    {renderTimeGroup('Mañana', Sunrise, groups.morning)}
                    {renderTimeGroup('Tarde', Sun, groups.afternoon)}
                    {renderTimeGroup('Noche', Moon, groups.evening)}
                    {slots.length === 0 && (
                      <p className="text-xs text-text-mute text-center py-3">Sin horarios</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Mobile placeholders with spinning flower */}
            {isTodas && slotsLoading && sortedDates.length < 4 && (
              <>
                {sortedDates.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-6">
                    <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin-slow" />
                    <p className="mt-2 text-sm text-brand-deep/70">Cargando horarios...</p>
                  </div>
                )}
                {Array.from({ length: 4 - sortedDates.length }).map((_, i) => (
                  <div key={`m-placeholder-${i}`} className="bg-surface-card rounded-card p-4 animate-pulse">
                    <div className="flex items-center gap-1.5 mb-2 pb-2 border-b border-brand-100">
                      <div className="w-3 h-3 bg-brand-100 rounded" />
                      <div className="w-20 h-4 bg-brand-100 rounded" />
                    </div>
                    <div className="space-y-2">
                      <div className="w-12 h-2.5 bg-brand-50 rounded" />
                      <div className="flex flex-wrap gap-1.5">
                        {Array.from({ length: 6 }).map((_, j) => (
                          <div key={j} className="h-8 w-16 bg-brand-50 rounded-card-sm" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ScheduleSection;
